import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { profileService } from '../services/api';
import MessagesSidebar from '../components/MessagesSidebar';

/* ─────────────── küçük UI bileşenleri ─────────────── */

const FormField = ({ label, hint, children }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-xs sm:text-sm font-bold text-[#204286] text-right">
            {label}
        </label>
        {children}
        {hint && (
            <p className="text-[#D0021B] text-[11px] sm:text-xs text-right">{hint}</p>
        )}
    </div>
);

const TextInput = ({ value, onChange, placeholder, type = 'text', disabled }) => (
    <input
        type={type}
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        dir="rtl"
        className="w-full bg-white border border-[#D6E1F5] rounded-xl px-4 py-2.5 text-xs sm:text-sm text-[#204286] placeholder:text-gray-300 focus:outline-none focus:ring-2 focus:ring-[#292DD8]/30 transition-all disabled:opacity-50"
    />
);

const DropdownInput = ({ value, onChange, options = [], placeholder, loading }) => (
    <div className="relative">
        <select
            value={value ?? ''}
            onChange={onChange}
            disabled={loading}
            className="w-full appearance-none bg-white border border-[#D6E1F5] rounded-xl px-4 py-2.5 text-xs sm:text-sm text-[#204286] focus:outline-none focus:ring-2 focus:ring-[#292DD8]/30 transition-all disabled:opacity-50"
        >
            <option value="">{loading ? 'در حال بارگذاری...' : placeholder}</option>
            {options.map((opt) => (
                <option key={opt.slug} value={opt.slug}>
                    {opt.name}
                </option>
            ))}
        </select>
        <svg
            className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#292DD8] pointer-events-none"
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    </div>
);

/* ─────────────── toast ─────────────── */
const Toast = ({ message, type, onClose }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-xl text-white text-sm font-bold shadow-xl ${
            type === 'success' ? 'bg-green-500' : 'bg-red-500'
        }`}
    >
        <div className="flex items-center gap-2">
            <span>{message}</span>
            <button onClick={onClose} className="opacity-70 hover:opacity-100">✕</button>
        </div>
    </motion.div>
);

/* ─────────────── main component ─────────────── */
const Account = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const isRtl = language === 'fa';
    const t = (fa, en) => (isRtl ? fa : en);

    /* avatar */
    const fileInputRef = useRef(null);
    const [avatarUrl, setAvatarUrl] = useState(null);
    const [avatarUploading, setAvatarUploading] = useState(false);

    /* page loading */
    const [pageLoading, setPageLoading] = useState(true);
    const [pageError, setPageError] = useState(null);

    /* radio */
    const [personType, setPersonType] = useState('legal'); // 'real' | 'legal'

    /* options */
    const [provinces, setProvinces] = useState([]);
    const [scopes, setScopes] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(true);

    /* natural form */
    const [natural, setNatural] = useState({
        name: '', identification: '', phone: '', email: '',
        province: '', gender: ''
    });

    /* legal form */
    const [legal, setLegal] = useState({
        name: '', phone: '', identification: '', ceo_name: '',
        contact_name: '', contact_phone: '', province: '',
        scope: '', knowledge_enterprise: '', website: ''
    });

    /* validation errors — حقیقی و حقوقی کاملاً جدا */
    const [naturalErrors, setNaturalErrors] = useState({});
    const [legalErrors, setLegalErrors] = useState({});

    /* submit state */
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState(null); // { message, type }

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── initial data fetch ── */
    useEffect(() => {
        const init = async () => {
            setPageLoading(true);
            try {
                const [profile, provincesData, scopesData] = await Promise.all([
                    profileService.getProfile(),
                    profileService.getProvinces(),
                    profileService.getScopes()
                ]);

                setAvatarUrl(profile.picture || null);
                setProvinces(provincesData || []);
                setScopes(scopesData || []);

                // natural form pre-fill
                const n = profile.natural || {};
                setNatural({
                    name: n.name || '',
                    identification: n.identification || '',
                    phone: n.phone || '',
                    email: n.email || '',
                    province: n.province?.slug || '',
                    gender: n.gender !== undefined && n.gender !== null ? String(n.gender) : ''
                });

                // legal form pre-fill
                const l = profile.legal || {};
                setLegal({
                    name: l.name || '',
                    phone: l.phone || '',
                    identification: l.identification || '',
                    ceo_name: l.ceo_name || '',
                    contact_name: l.contact_name || '',
                    contact_phone: l.contact_phone || '',
                    province: l.province?.slug || '',
                    scope: l.scope?.slug || '',
                    knowledge_enterprise:
                        l.knowledge_enterprise !== undefined && l.knowledge_enterprise !== null
                            ? String(l.knowledge_enterprise)
                            : '',
                    website: l.website || ''
                });

                // hangi form dolu ise onu göster
                if (profile.natural?.name && !profile.legal?.name) setPersonType('real');
                else setPersonType('legal');

            } catch (err) {
                console.error('Profile fetch error:', err);
                setPageError(t('خطا در دریافت اطلاعات', 'Error loading profile'));
            } finally {
                setPageLoading(false);
                setOptionsLoading(false);
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── avatar ── */
    const handleAvatarChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setAvatarUploading(true);
        try {
            const data = await profileService.uploadPicture(file);
            setAvatarUrl(data.picture || null);
            showToast(t('عکس با موفقیت آپلود شد', 'Photo uploaded successfully'));
        } catch (err) {
            console.error('Upload error:', err);
            showToast(t('خطا در آپلود عکس', 'Error uploading photo'), 'error');
        } finally {
            setAvatarUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemoveAvatar = async () => {
        if (!avatarUrl) return;
        setAvatarUploading(true);
        try {
            await profileService.deletePicture();
            setAvatarUrl(null);
            showToast(t('عکس حذف شد', 'Photo removed'));
        } catch (err) {
            console.error('Delete avatar error:', err);
            showToast(t('خطا در حذف عکس', 'Error removing photo'), 'error');
        } finally {
            setAvatarUploading(false);
        }
    };

    /* ── validation ──
       هیچ فیلدی الزامی نیست؛ فقط اگر پر شده باشد فرمتش چک می‌شود.
       توجه: شماره تلفن غیرقابل‌ویرایش است، پس از اعتبارسنجی مستثنی است. */
    const validateNatural = (data) => {
        const errs = {};

        if (data.identification && data.identification.trim() && !/^\d{10}$/.test(data.identification.trim())) {
            errs.identification = t('کد ملی باید ۱۰ رقم باشد', 'National code must be 10 digits');
        }

        if (data.email && data.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
            errs.email = t('ایمیل معتبر نیست', 'Email is invalid');
        }

        return errs;
    };

    const validateLegal = (data) => {
        const errs = {};

        if (data.identification && data.identification.trim() && !/^\d{10,11}$/.test(data.identification.trim())) {
            errs.identification = t('شناسه ملی معتبر نیست', 'National ID is invalid');
        }

        if (data.contact_phone && data.contact_phone.trim() && !/^09\d{9}$/.test(data.contact_phone.trim())) {
            errs.contact_phone = t('شماره رابط معتبر نیست', 'Contact phone is invalid');
        }

        if (data.website && data.website.trim() && !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/i.test(data.website.trim())) {
            errs.website = t('آدرس سایت معتبر نیست', 'Website is invalid');
        }

        return errs;
    };

    /* ── submit ── */
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (personType === 'real') {
            const validationErrors = validateNatural(natural);
            if (Object.keys(validationErrors).length > 0) {
                setNaturalErrors(validationErrors);
                showToast(
                    t('لطفاً خطاهای فرم را برطرف کنید', 'Please fix the form errors'),
                    'error'
                );
                return;
            }
            setNaturalErrors({});
        } else {
            const validationErrors = validateLegal(legal);
            if (Object.keys(validationErrors).length > 0) {
                setLegalErrors(validationErrors);
                showToast(
                    t('لطفاً خطاهای فرم را برطرف کنید', 'Please fix the form errors'),
                    'error'
                );
                return;
            }
            setLegalErrors({});
        }

        setSubmitting(true);
        try {
            if (personType === 'real') {
                await profileService.updateNatural({
                    name: natural.name || undefined,
                    identification: natural.identification || undefined,
                    phone: natural.phone || undefined,
                    email: natural.email || undefined,
                    province: natural.province || undefined,
                    gender: natural.gender !== '' ? Number(natural.gender) : undefined
                });
            } else {
                await profileService.updateLegal({
                    name: legal.name || undefined,
                    phone: legal.phone || undefined,
                    identification: legal.identification || undefined,
                    ceo_name: legal.ceo_name || undefined,
                    contact_name: legal.contact_name || undefined,
                    contact_phone: legal.contact_phone || undefined,
                    province: legal.province || undefined,
                    scope: legal.scope || undefined,
                    knowledge_enterprise:
                        legal.knowledge_enterprise !== ''
                            ? legal.knowledge_enterprise === 'true'
                            : undefined,
                    website: legal.website || undefined
                });
            }
            showToast(t('اطلاعات با موفقیت ذخیره شد', 'Information saved successfully'));
        } catch (err) {
            console.error('Update error:', err);
            showToast(
                t('خطا در ذخیره اطلاعات. دوباره تلاش کنید.', 'Error saving. Please try again.'),
                'error'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const setN = (field) => (e) => {
        setNatural((p) => ({ ...p, [field]: e.target.value }));
        setNaturalErrors((p) => ({ ...p, [field]: undefined }));
    };
    const setL = (field) => (e) => {
        setLegal((p) => ({ ...p, [field]: e.target.value }));
        setLegalErrors((p) => ({ ...p, [field]: undefined }));
    };

    /* ── page loading / error states ── */
    if (pageLoading) {
        return (
            <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-8 font-iransansRegular flex justify-center items-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-[#292DD8] border-t-transparent rounded-full animate-spin" />
                    <p className="text-[#292DD8] font-bold text-sm">
                        {t('در حال بارگذاری...', 'Loading...')}
                    </p>
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="max-w-[1136px] mx-auto px-3 sm:px-4 py-8 font-iransansRegular flex justify-center items-center min-h-[60vh]">
                <div className="flex flex-col items-center gap-4 bg-red-50 rounded-xl p-8">
                    <p className="text-red-600 font-bold text-sm">{pageError}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-5 py-2 bg-red-500 text-white rounded-lg text-sm font-bold hover:bg-red-600 transition-colors"
                    >
                        {t('تلاش مجدد', 'Try Again')}
                    </button>
                </div>
            </div>
        );
    }

    /* ─────────────── render ─────────────── */
    return (
        <div
            className="max-w-[1136px] mx-auto px-3 sm:px-4 py-4 lg:py-8 font-iransansRegular overflow-hidden"
            dir={isRtl ? 'rtl' : 'ltr'}
        >
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
                <MessagesSidebar
                    activeKey="account"
                    onNavigate={(key) => {
                        if (key === 'messages') navigate('/messages');
                        if (key === 'finance') navigate('/finance');
                        if (key === 'account') navigate('/account');
                        if (key === 'support') navigate('/support');
                        if (key === 'logout') navigate('/logout');
                    }}
                />

                <div className="flex-1 w-full min-w-0">
                    {/* Title */}
                    <motion.h1
                        className="text-xl sm:text-2xl lg:text-[32px] font-extrabold text-[#292DD8] mb-6 lg:mb-8 text-right"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        {t('حساب کاربری', 'User Account')}
                    </motion.h1>

                    <motion.form
                        onSubmit={handleSubmit}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        {/* Avatar + radio row */}
                        <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-8 mb-7">
                            {/* Avatar */}
                            <div className="flex items-center gap-3 shrink-0">
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gray-200 overflow-hidden shrink-0 relative">
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                    )}
                                    {avatarUploading && (
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-full">
                                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        </div>
                                    )}
                                </div>
                                <div className="flex flex-col gap-2">
                                    <label
                                        htmlFor="avatar-upload"
                                        className={`px-3 py-1.5 border border-[#204286] rounded-lg text-[#204286] text-xs sm:text-sm font-bold cursor-pointer hover:bg-[#EBF1FB] transition-colors whitespace-nowrap ${avatarUploading ? 'opacity-50 pointer-events-none' : ''}`}
                                    >
                                        {t('افزودن عکس', 'Add Photo')}
                                    </label>
                                    <input
                                        id="avatar-upload"
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleAvatarChange}
                                        className="hidden"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveAvatar}
                                        disabled={!avatarUrl || avatarUploading}
                                        className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-500 text-xs sm:text-sm font-bold hover:bg-gray-50 transition-colors whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        {t('حذف عکس', 'Remove Photo')}
                                    </button>
                                </div>
                            </div>

                            {/* Radio حقیقی / حقوقی */}
                            <div className="flex flex-col gap-2.5 mt-1 sm:mt-3">
                                {[
                                    { value: 'real', label: t('حقیقی', 'Individual') },
                                    { value: 'legal', label: t('حقوقی', 'Corporate') },
                                ].map((opt) => (
                                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer" onClick={() => setPersonType(opt.value)}>
                                        <span className="text-sm sm:text-base text-[#204286] font-medium">{opt.label}</span>
                                        <div className={`w-4 h-4 sm:w-[18px] sm:h-[18px] rounded-full border-2 flex items-center justify-center transition-all ${personType === opt.value ? 'border-[#292DD8]' : 'border-gray-400'}`}>
                                            {personType === opt.value && (
                                                <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-[#292DD8]" />
                                            )}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* ── REAL PERSON FORM ── */}
                        <AnimatePresence mode="wait">
                            {personType === 'real' && (
                                <motion.div
                                    key="real"
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.25 }}
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4 sm:gap-y-5 mb-7"
                                >
                                    <FormField
                                        label={t('نام', 'Name')}
                                        hint={t('«از حروف فارسی استفاده کنید»', '«Use Persian characters»')}
                                    >
                                        <TextInput
                                            value={natural.name}
                                            onChange={setN('name')}
                                            placeholder={t('مثال: علی محمدی', 'Example: Ali')}
                                        />
                                    </FormField>

                                    <FormField label={t('کد ملی', 'National Code')} hint={naturalErrors.identification}>
                                        <TextInput
                                            value={natural.identification}
                                            onChange={setN('identification')}
                                            placeholder={t('مثال: ۱۲۳۴۵۶۷۸۹۰', 'Example: 1234567890')}
                                        />
                                    </FormField>

                                    <FormField label={t('تلفن همراه', 'Mobile')} hint={t('این فیلد قابل ویرایش نیست', 'This field cannot be edited')}>
                                        <TextInput
                                            value={natural.phone}
                                            onChange={setN('phone')}
                                            placeholder="مثال: ۰۹۱۲۳۴۵۶۷۸۹"
                                            disabled
                                        />
                                    </FormField>

                                    <FormField label={t('ایمیل', 'Email')} hint={naturalErrors.email}>
                                        <TextInput
                                            value={natural.email}
                                            onChange={setN('email')}
                                            placeholder="مثال: ۰۹۱۲۳۴۵۶۷۸۹"
                                            type="email"
                                        />
                                    </FormField>

                                    <FormField label={t('شهر', 'City')}>
                                        <DropdownInput
                                            value={natural.province}
                                            onChange={setN('province')}
                                            options={provinces}
                                            placeholder={t('انتخاب کنید', 'Select')}
                                            loading={optionsLoading}
                                        />
                                    </FormField>

                                    <FormField label={t('جنسیت', 'Gender')}>
                                        <DropdownInput
                                            value={natural.gender}
                                            onChange={setN('gender')}
                                            options={[
                                                { slug: '0', name: t('آقا', 'Male') },
                                                { slug: '1', name: t('خانم', 'Female') }
                                            ]}
                                            placeholder={t('انتخاب کنید', 'Select')}
                                        />
                                    </FormField>
                                </motion.div>
                            )}

                            {/* ── LEGAL PERSON FORM ── */}
                            {personType === 'legal' && (
                                <motion.div
                                    key="legal"
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.25 }}
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4 sm:gap-y-5 mb-7"
                                >
                                    <FormField
                                        label={t('نام کامل شرکت', 'Company Full Name')}
                                        hint={t('«از حروف فارسی استفاده کنید»', '«Use Persian characters»')}
                                    >
                                        <TextInput
                                            value={legal.name}
                                            onChange={setL('name')}
                                            placeholder={t('مثال: شرکت مهرگستر امید', 'Example: Company Name')}
                                        />
                                    </FormField>

                                    <FormField label={t('شناسه ملی', 'National ID')} hint={legalErrors.identification}>
                                        <TextInput
                                            value={legal.identification}
                                            onChange={setL('identification')}
                                            placeholder={t('مثال: شرکت مهرگستر امید', 'Example: 10100000000')}
                                        />
                                    </FormField>

                                    <FormField label={t('نام مدیرعامل', 'CEO Name')}>
                                        <TextInput
                                            value={legal.ceo_name}
                                            onChange={setL('ceo_name')}
                                            placeholder={t('مثال: شرکت مهرگستر امید', 'Example: Ahmad Rezaei')}
                                        />
                                    </FormField>

                                    <FormField label={t('شماره شرکت', 'Company Phone')}>
                                        <TextInput
                                            value={legal.phone}
                                            onChange={setL('phone')}
                                            placeholder="مثال: ۰۹۱۲۳۴۵۶۷۸۹"
                                        />
                                    </FormField>

                                    <FormField label={t('نام رابط', 'Contact Name')}>
                                        <TextInput
                                            value={legal.contact_name}
                                            onChange={setL('contact_name')}
                                            placeholder={t('مثال: شرکت مهرگستر امید', 'Example: Sara Ahmadi')}
                                        />
                                    </FormField>

                                    <FormField label={t('شماره رابط', 'Contact Phone')} hint={legalErrors.contact_phone}>
                                        <TextInput
                                            value={legal.contact_phone}
                                            onChange={setL('contact_phone')}
                                            placeholder="مثال: ۰۹۱۲۳۴۵۶۷۸۹"
                                        />
                                    </FormField>

                                    <FormField label={t('استان', 'Province')}>
                                        <DropdownInput
                                            value={legal.province}
                                            onChange={setL('province')}
                                            options={provinces}
                                            placeholder={t('انتخاب کنید', 'Select')}
                                            loading={optionsLoading}
                                        />
                                    </FormField>

                                    <FormField label={t('حوزه', 'Scope')}>
                                        <DropdownInput
                                            value={legal.scope}
                                            onChange={setL('scope')}
                                            options={scopes}
                                            placeholder={t('انتخاب کنید', 'Select')}
                                            loading={optionsLoading}
                                        />
                                    </FormField>

                                    <FormField label={t('دانش بنیان', 'Knowledge-Based')}>
                                        <DropdownInput
                                            value={legal.knowledge_enterprise}
                                            onChange={setL('knowledge_enterprise')}
                                            options={[
                                                { slug: 'true', name: t('بله', 'Yes') },
                                                { slug: 'false', name: t('خیر', 'No') }
                                            ]}
                                            placeholder={t('انتخاب کنید', 'Select')}
                                        />
                                    </FormField>

                                    <FormField label={t('سایت', 'Website')} hint={legalErrors.website}>
                                        <TextInput
                                            value={legal.website}
                                            onChange={setL('website')}
                                            placeholder="مثال: ۰۹۱۲۳۴۵۶۷۸۹"
                                        />
                                    </FormField>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Submit */}
                        <div className="flex justify-center mt-2">
                            <motion.button
                                type="submit"
                                disabled={submitting}
                                whileHover={{ scale: submitting ? 1 : 1.02 }}
                                whileTap={{ scale: submitting ? 1 : 0.98 }}
                                className="px-10 py-2.5 rounded-lg text-white font-bold text-sm sm:text-base shadow-lg bg-gradient-to-r from-[#292DD8] to-[#4B4FEE] hover:from-[#1f23a8] hover:to-[#3a3ecc] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {submitting
                                    ? t('در حال ذخیره...', 'Saving...')
                                    : t('ذخیره اطلاعات', 'Save Information')}
                            </motion.button>
                        </div>
                    </motion.form>
                </div>
            </div>

            {/* Toast notification */}
            <AnimatePresence>
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default Account;